import React, { useState, useEffect } from 'react';
import { CreditCard, Shield, Key, DollarSign, ArrowLeft, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import NavigationBar from '../components/NavigationBar';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { isBusinessOwner } from '../utils/roleUtils';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  features: string[];
  status: 'active' | 'cancelled' | 'expired';
  nextBillingDate: string;
}

interface PaymentMethod {
  id: string;
  type: 'card' | 'bank';
  last4: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
}

interface PlanOption {
  id: string;
  name: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  features: string[];
  popular?: boolean;
  current?: boolean;
}

const BusinessAccountSettings: React.FC = () => {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  
  const [activeTab, setActiveTab] = useState<'subscription' | 'payment' | 'password' | 'plans'>('subscription');
  const [isLoading, setIsLoading] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionPlan | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<PlanOption[]>([]);

  // Mock subscription data - replace with actual API call
  useEffect(() => {
    // Simulate API call
    setSubscription({
      id: 'sub_123',
      name: 'Professional Plan',
      price: 29.99,
      billingCycle: 'monthly',
      features: [
        'Unlimited appointments',
        'Customer management',
        'SMS notifications',
        'Analytics dashboard',
        'Custom branding'
      ],
      status: 'active',
      nextBillingDate: '2025-02-25'
    });

    setPaymentMethods([
      {
        id: 'pm_123',
        type: 'card',
        last4: '4242',
        brand: 'Visa',
        expiryMonth: 12,
        expiryYear: 2026,
        isDefault: true
      }
    ]);

    // Mock available plans data
    setAvailablePlans([
      {
        id: 'basic',
        name: 'Basic',
        price: 19.99,
        billingCycle: 'monthly',
        features: [
          'Up to 100 appointments/month',
          'Basic customer management',
          'Email notifications',
          'Standard support'
        ]
      },
      {
        id: 'plus',
        name: 'Plus',
        price: 39.99,
        billingCycle: 'monthly',
        features: [
          'Up to 500 appointments/month',
          'Advanced customer management',
          'SMS notifications',
          'Analytics dashboard',
          'Priority support',
          'Custom branding'
        ],
        popular: true
      },
      {
        id: 'pro',
        name: 'Pro',
        price: 79.99,
        billingCycle: 'monthly',
        features: [
          'Unlimited appointments',
          'Full customer management',
          'SMS & Email notifications',
          'Advanced analytics',
          'Priority support',
          'Custom branding',
          'API access',
          'White-label options'
        ],
        current: true
      }
    ]);
  }, []);

  // Check if user is a Business Owner - must be after all hooks
  if (!user || !currentBusiness || !isBusinessOwner(user)) {
    return <Navigate to="/dashboard/business" replace />;
  }

  const handlePasswordReset = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Implement actual password reset API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast.success('Password updated successfully');
      setShowPasswordReset(false);
    } catch (error) {
      toast.error('Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPaymentMethod = async (cardData: any) => {
    setIsLoading(true);
    try {
      // TODO: Implement actual payment method addition API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast.success('Payment method added successfully');
      setShowAddCard(false);
    } catch (error) {
      toast.error('Failed to add payment method');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderSubscriptionSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Current Subscription</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            subscription?.status === 'active' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {subscription?.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </div>

        {subscription && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-semibold text-gray-900">{subscription.name}</h4>
                <p className="text-sm text-gray-600">
                  ${subscription.price}/{subscription.billingCycle === 'monthly' ? 'month' : 'year'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Next billing</p>
                <p className="font-medium text-gray-900">{formatDate(subscription.nextBillingDate)}</p>
              </div>
            </div>

            <div>
              <h5 className="font-medium text-gray-900 mb-2">Plan Features</h5>
                             <ul className="space-y-1">
                 {subscription.features.map((feature, index) => (
                   <li key={index} className="flex items-center text-sm text-gray-600">
                     <Check className="w-4 h-4 text-green-500 mr-2" />
                     {feature}
                   </li>
                 ))}
               </ul>
            </div>

            <div className="flex space-x-3 pt-4">
              <button className="aacore-btn2-primary">
                Upgrade Plan
              </button>
              <button className="aacore-btn2-outline">
                Cancel Subscription
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPaymentSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
          <button
            onClick={() => setShowAddCard(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Payment Method
          </button>
        </div>

        <div className="space-y-3">
          {paymentMethods.map((method) => (
            <div key={method.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">
                    {method.brand} •••• {method.last4}
                  </p>
                  <p className="text-sm text-gray-600">
                    Expires {method.expiryMonth}/{method.expiryYear}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {method.isDefault && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Default
                  </span>
                )}
                <button className="text-blue-600 hover:text-blue-700 text-sm">
                  Edit
                </button>
                <button className="text-red-600 hover:text-red-700 text-sm">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing History</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">January 2025</p>
              <p className="text-sm text-gray-600">Professional Plan</p>
            </div>
            <div className="text-right">
              <p className="font-medium text-gray-900">$29.99</p>
              <p className="text-sm text-green-600">Paid</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">December 2024</p>
              <p className="text-sm text-gray-600">Professional Plan</p>
            </div>
            <div className="text-right">
              <p className="font-medium text-gray-900">$29.99</p>
              <p className="text-sm text-green-600">Paid</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPasswordSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Account Security</h3>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Password Security</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Keep your account secure by using a strong password and updating it regularly.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <button
              onClick={() => setShowPasswordReset(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Change Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSubscriptionPlansSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Available Plans</h3>
          <p className="text-sm text-gray-600 mt-1">Choose the plan that best fits your business needs</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {availablePlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-lg border-2 p-6 transition-all hover:shadow-md ${
                plan.current
                  ? 'border-blue-500 bg-blue-50'
                  : plan.popular
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              
              {plan.current && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Current Plan
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{plan.name}</h4>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-600">/{plan.billingCycle === 'monthly' ? 'month' : 'year'}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  plan.current
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : plan.popular
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                disabled={plan.current}
              >
                {plan.current ? 'Current Plan' : 'Select Plan'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      
      <div className="flex">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 bg-white shadow-sm border-r border-gray-200 min-h-screen">
          <div className="p-4 h-full flex flex-col overflow-hidden">
            <div className="mb-6">
              <h1 className="text-xl font-semibold text-gray-900">Account Settings</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your business account</p>
            </div>

                         <nav className="flex-1 overflow-y-auto scrollbar-hide">
               <div className="space-y-1">
                 {/* Desktop Back to Dashboard - hidden on mobile */}
                 <div className="hidden lg:block">
                   <Link
                     to="/dashboard/business"
                     className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors text-gray-700 hover:bg-gray-100"
                   >
                     <ArrowLeft className="w-5 h-5" />
                     <span>Back to Dashboard</span>
                   </Link>
                   
                   <div className="border-t border-gray-200 my-2"></div>
                 </div>
                 
                 <button
                   onClick={() => setActiveTab('subscription')}
                   className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                     activeTab === 'subscription'
                       ? 'bg-blue-50 text-blue-700 border border-blue-200'
                       : 'text-gray-700 hover:bg-gray-100'
                   }`}
                 >
                   <DollarSign className="w-5 h-5" />
                   <span>Subscription</span>
                 </button>

                <button
                  onClick={() => setActiveTab('payment')}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'payment'
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Payment Information</span>
                </button>

                                 <button
                   onClick={() => setActiveTab('password')}
                   className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                     activeTab === 'password'
                       ? 'bg-blue-50 text-blue-700 border border-blue-200'
                       : 'text-gray-700 hover:bg-gray-100'
                   }`}
                 >
                   <Key className="w-5 h-5" />
                   <span>Password & Security</span>
                 </button>

                 <button
                   onClick={() => setActiveTab('plans')}
                   className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                     activeTab === 'plans'
                       ? 'bg-blue-50 text-blue-700 border border-blue-200'
                       : 'text-gray-700 hover:bg-gray-100'
                   }`}
                 >
                   <DollarSign className="w-5 h-5" />
                   <span>Subscription Plans</span>
                 </button>
              </div>
            </nav>
          </div>
        </div>

                 {/* Main Content */}
         <div className="flex-1 lg:ml-0">
           {/* Mobile Tab Navigation */}
           <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
             <div className="flex space-x-1">
               <button
                 onClick={() => setActiveTab('subscription')}
                 className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                   activeTab === 'subscription'
                     ? 'bg-blue-600 text-white'
                     : 'text-gray-600 hover:text-gray-900'
                 }`}
               >
                 Subscription
               </button>
               <button
                 onClick={() => setActiveTab('payment')}
                 className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                   activeTab === 'payment'
                     ? 'bg-blue-600 text-white'
                     : 'text-gray-600 hover:text-gray-900'
                 }`}
               >
                 Payment
               </button>
                               <button
                  onClick={() => setActiveTab('password')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'password'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Security
                </button>
                <button
                  onClick={() => setActiveTab('plans')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'plans'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Plans
                </button>
             </div>
           </div>

                                   <div className="p-4 md:p-8 lg:pb-0 pb-20">
              {activeTab === 'subscription' && renderSubscriptionSection()}
              {activeTab === 'payment' && renderPaymentSection()}
              {activeTab === 'password' && renderPasswordSection()}
              {activeTab === 'plans' && renderSubscriptionPlansSection()}
            </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
            <PasswordResetForm
              onSubmit={handlePasswordReset}
              onCancel={() => setShowPasswordReset(false)}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}

      {/* Add Payment Method Modal */}
      {showAddCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Payment Method</h3>
            <PaymentMethodForm
              onSubmit={handleAddPaymentMethod}
              onCancel={() => setShowAddCard(false)}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}
      
      {/* Mobile Back to Dashboard Button - Fixed at bottom */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40">
        <Link
          to="/dashboard/business"
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>
      </div>
    </div>
  );
};

// Password Reset Form Component
interface PasswordResetFormProps {
  onSubmit: (currentPassword: string, newPassword: string, confirmPassword: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onSubmit, onCancel, isLoading }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(currentPassword, newPassword, confirmPassword);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Current Password
        </label>
        <input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          New Password
        </label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Confirm New Password
        </label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Updating...' : 'Update Password'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Payment Method Form Component
interface PaymentMethodFormProps {
  onSubmit: (cardData: any) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({ onSubmit, onCancel, isLoading }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      cardNumber,
      expiryMonth,
      expiryYear,
      cvv,
      cardholderName
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Card Number
        </label>
        <input
          type="text"
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
          placeholder="1234 5678 9012 3456"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expiry Month
          </label>
          <select
            value={expiryMonth}
            onChange={(e) => setExpiryMonth(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Month</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
              <option key={month} value={month.toString().padStart(2, '0')}>
                {month.toString().padStart(2, '0')}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expiry Year
          </label>
          <select
            value={expiryYear}
            onChange={(e) => setExpiryYear(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Year</option>
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CVV
          </label>
          <input
            type="text"
            value={cvv}
            onChange={(e) => setCvv(e.target.value)}
            placeholder="123"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cardholder Name
          </label>
          <input
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="John Doe"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
      </div>

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Adding...' : 'Add Payment Method'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default BusinessAccountSettings; 